import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import displayError from '../../utils/ErrorHandler';
import UserContext from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import authenticate from '../../utils/Authenticate';

const ZabihatCommitmentDetails = () => {
  const [commitments, setCommitments] = useState([]);
  const [formStates, setFormStates] = useState({});
  const [fileStates, setFileStates] = useState({});
  const [errors, setErrors] = useState({});
  const [messages, setMessages] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      if (!user) {
        await authenticate(setUser, navigate);
      }
    };
    init();
  }, [user, setUser, navigate]);

  const fetchCommitments = async () => {
    if (!user?.Mauze) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/zabihatCommitment/get?jamiat=${user.Mauze}`,
        { withCredentials: true }
      );
      const rawData = res.data.data;
      const parsed = rawData.map((raw) => ({
        _id: raw._id,
        quantity: raw.quantity,
        contactName: raw.contactName,
        contactEmail: raw.contactEmail,
        contactNumber: raw.contactNumber,
        estimatedDeliveryDate: raw.estimatedDeliveryDate?.substring(0, 10) || '',
        status: raw.status,
        ewayBill: raw.ewayBill || '',
        silatItemId: raw.silatZabihat._id,
        silatItemName: raw.silatZabihat.name,
        silatItemUnit: raw.silatZabihat.unit,
        jamiatId: raw.jamiat._id,
        jamiatName: raw.jamiat.jamiat_name,
      }));

      setCommitments(parsed);

      const initialFormStates = {};
      const initialErrors = {};
      parsed.forEach((commit) => {
        initialFormStates[commit._id] = { ...commit };
        initialErrors[commit._id] = {};
      });

      setFormStates(initialFormStates);
      setErrors(initialErrors);
      setMessages({});
      setFileStates({});
    } catch (err) {
      setMessages({ general: displayError(err) || 'Failed to fetch commitments.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommitments();
  }, [user]);

  const validateField = (name, value) => {
    switch (name) {
      case 'contactName':
        return !value?.trim() ? 'Contact name is required.' : '';
      case 'contactEmail':
        return !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value) ? 'Invalid email format.' : '';
      case 'contactNumber':
        return !/^\d{10,}$/.test(value) ? 'Contact number must be at least 10 digits.' : '';
      case 'estimatedDeliveryDate':
        return !value ? 'Estimated delivery date is required.' : '';
      default:
        return '';
    }
  };

  const handleInputChange = (id, name, value) => {
    setFormStates((prev) => ({
      ...prev,
      [id]: { ...prev[id], [name]: value },
    }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [name]: error },
    }));
  };

  const handleFileChange = (id, file) => {
    setFileStates((prev) => ({ ...prev, [id]: file }));
    setErrors((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), billFile: '' } }));
  };

  const hasErrors = (id) => {
    if (!errors[id]) return false;
    return Object.values(errors[id]).some(Boolean);
  };

  const handleSubmit = async (id) => {
    const formData = formStates[id];
    const requiredFields = ['contactName', 'contactEmail', 'contactNumber', 'estimatedDeliveryDate'];
    let validationErrors = {};

    requiredFields.forEach((field) => {
      const err = validateField(field, formData[field]);
      if (err) validationErrors[field] = err;
    });

    if (Object.values(validationErrors).some(Boolean)) {
      setErrors((prev) => ({ ...prev, [id]: validationErrors }));
      setMessages((prev) => ({ ...prev, [id]: 'Please fix validation errors.' }));
      return;
    }

    try {
      setLoading(true);
      const form = new FormData();
      form.append('_id', formData._id);
      form.append('quantity', formData.quantity);
      form.append('contactName', formData.contactName);
      form.append('contactEmail', formData.contactEmail);
      form.append('contactNumber', formData.contactNumber);
      form.append('estimatedDeliveryDate', formData.estimatedDeliveryDate);
      form.append('status', formData.status);

      const billFile = fileStates[id];
      if (billFile) {
        form.append('bill', billFile);
      } else if (formData.ewayBill) {
        form.append('bill', formData.ewayBill);
      }

      const res = await axios.put(`${process.env.REACT_APP_API_URI}/api/commit/zabihat-commit/update`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });

      await fetchCommitments();

      setMessages((prev) => ({ ...prev, [id]: 'Commitment updated successfully!' }));
      setFileStates((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    } catch (err) {
      setMessages((prev) => ({ ...prev, [id]: displayError(err) || 'Update failed.' }));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (id) => {
    // Reset form state to last saved commitment data
    const original = commitments.find((c) => c._id === id);
    if (original) {
      setFormStates((prev) => ({ ...prev, [id]: { ...original } }));
      setErrors((prev) => ({ ...prev, [id]: {} }));
      setMessages((prev) => ({ ...prev, [id]: '' }));
      setFileStates((prev) => ({ ...prev, [id]: null }));
    }
  };

  const renderBillPreview = (ewayBill) => {
    if (!ewayBill) return null;
    const isPDF = ewayBill.toLowerCase().endsWith('.pdf');
    return (
      <div className="mb-4">
        <label className="block font-semibold mb-2 text-gray-700">Uploaded Bill:</label>
        {isPDF ? (
          <embed src={ewayBill} type="application/pdf" className="w-full max-h-[500px]" />
        ) : (
          <img src={ewayBill} alt="Uploaded Bill" className="w-full max-w-full h-auto object-contain rounded-md" />
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f7f7] font-sans">
      <main className="flex-grow max-w-6xl mx-auto px-6 py-10 w-full">
        <h2 className="text-4xl font-semibold text-[#3C2E00] mb-8 tracking-tight">
          Zabihat Commitments
        </h2>

        {messages.general && (
          <div className="mb-6 text-center text-red-600 font-semibold">{messages.general}</div>
        )}

        {loading && (
          <div className="p-6 text-center text-gray-700 font-medium">Loading commitments...</div>
        )}

        {!loading && commitments.length === 0 && (
          <div className="p-6 text-center text-red-600 font-semibold">No commitments found.</div>
        )}

        <div className="space-y-4">
          {commitments.map((commit) => {
            const isExpanded = expandedId === commit._id;
            const form = formStates[commit._id] || {};
            const err = errors[commit._id] || {};
            const msg = messages[commit._id] || '';

            return (
              <div
                key={commit._id}
                className="bg-white rounded-lg shadow-md border border-gray-300 border-l-8 border-[#D4A12A]"
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(isExpanded ? null : commit._id)}
                  className="w-full flex justify-between items-center px-8 py-4 focus:outline-none"
                >
                  <div className="text-xl font-semibold text-[#3C2E00] flex items-center space-x-2">
                    <span>{commit.silatItemName}</span>
                    <span className="text-sm font-normal italic text-gray-600">({commit.silatItemUnit})</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-gray-600 italic select-text">
                      Quantity: <span className="font-semibold">{commit.quantity}</span>
                    </div>
                    <svg
                      className={`w-6 h-6 text-[#D4A12A] transform transition-transform duration-300 ${
                        isExpanded ? 'rotate-180' : 'rotate-0'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-8 pb-8 space-y-6 border-t border-gray-300">
                    {msg && (
                      <div
                        className={`rounded-md px-6 py-4 font-semibold text-center text-lg
                          ${
                            msg.toLowerCase().includes('success')
                              ? 'bg-green-600 text-white border border-green-700'
                              : 'bg-red-600 text-white border border-red-700'
                          }
                        `}
                      >
                        {msg}
                      </div>
                    )}

                    {/* Status & Jamiat Info */}
                    <div className="grid gap-6 md:grid-cols-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Jamiat:</label>
                        <div className="bg-[#f9fafc] text-gray-600 italic p-3 rounded border border-gray-200 shadow-sm select-text">
                          {commit.jamiatName}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Status:</label>
                        <div className="bg-[#f9fafc] text-gray-600 italic p-3 rounded border border-gray-200 shadow-sm select-text">
                          {commit.status}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity:</label>
                        <div className="bg-[#f9fafc] text-gray-600 italic p-3 rounded border border-gray-200 shadow-sm select-text">
                          {commit.quantity}
                        </div>
                      </div>
                    </div>

                    {/* Editable fields */}
                    <div className="grid gap-6 md:grid-cols-2">
                      {[
                        { label: 'Contact Name', name: 'contactName', type: 'text' },
                        { label: 'Contact Email', name: 'contactEmail', type: 'email' },
                        { label: 'Contact Number', name: 'contactNumber', type: 'text' },
                        { label: 'Estimated Delivery Date', name: 'estimatedDeliveryDate', type: 'date' },
                      ].map(({ label, name, type }) => (
                        <div key={name}>
                          <label
                            htmlFor={`${commit._id}-${name}`}
                            className="block text-sm font-semibold text-gray-700 mb-3"
                          >
                            {label}:
                          </label>
                          <input
                            id={`${commit._id}-${name}`}
                            type={type}
                            name={name}
                            value={form[name] || ''}
                            onChange={(e) => handleInputChange(commit._id, name, e.target.value)}
                            className="w-full rounded-md border px-4 py-3 text-gray-800 font-normal tracking-wide
                              bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4A12A] transition"
                          />
                          {err[name] && (
                            <p className="mt-2 text-sm text-red-600 font-semibold">{err[name]}</p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* File upload */}
                    <div className="mb-6">
                      <label
                        htmlFor={`bill-${commit._id}`}
                        className="block font-semibold text-gray-700 mb-4 text-lg"
                      >
                        Upload Bill (PDF/Image):
                      </label>
                      <input
                        type="file"
                        id={`bill-${commit._id}`}
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(commit._id, e.target.files[0])}
                        className="w-full border border-gray-300 rounded-md p-3 text-gray-700 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-[#D4A12A]"
                      />
                      {err.billFile && (
                        <p className="mt-2 text-sm text-red-600 font-semibold">{err.billFile}</p>
                      )}
                    </div>

                    {/* Bill preview */}
                    {renderBillPreview(form.ewayBill)}

                    {/* Action buttons */}
                    <div className="flex space-x-4">
                      <button
                        onClick={() => handleSubmit(commit._id)}
                        disabled={hasErrors(commit._id)}
                        className={`rounded-md px-6 py-2 text-white font-semibold tracking-wide transition
                          ${
                            hasErrors(commit._id)
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-[#D4A12A] hover:bg-[#aa841a]'
                          }
                        `}
                      >
                        Submit
                      </button>
                      <button
                        onClick={() => handleCancel(commit._id)}
                        className="rounded-md px-6 py-2 border border-gray-400 text-gray-700 font-semibold hover:bg-gray-100 transition tracking-wide"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default ZabihatCommitmentDetails;
